import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useGlobal } from "utils/globalState";

import { Display4 } from "@sainsburys-tech/luna-typography";
import {
  trackEvent,
  reportError,
  NotificationStatuses,
} from "@jsainsburyplc/id-ui-common";

import { accountRecoveryUserData, OTP_FORM_ID } from "constants/mfaAndOtp";
import { actionTypes, categoryTypes } from "constants/tracking";
import { newCodeMessage, newCodeErrorMessage } from "constants/mfaAndOtp";
import { getResetPasswordLinkNoToken } from "utils/urls";
import { getErrorMessage } from "utils/otpForm";
import { getBlackBoxToken } from "utils/iovation";
import { otpService } from "services/otp.service";

import OtpForm from "components/OtpForm";
import PageFormat from "components/PageFormat";

const trackRequestCode = (action) => {
  trackEvent({
    category: categoryTypes.ACCOUNT_RECOVERY_OTP,
    label: "Request new OTP code",
    action,
  });
};

const trackFormSubmit = () => {
  trackEvent({
    category: categoryTypes.ACCOUNT_RECOVERY_OTP,
    action: actionTypes.SUBMIT,
    label: "Submit OTP code",
  });
};

const trackFormSubmitSuccess = () => {
  trackEvent({
    category: categoryTypes.ACCOUNT_RECOVERY_OTP,
    action: actionTypes.SUCCESS,
    label: "Submit OTP code",
  });
};

const trackFormSubmitError = (code) => {
  trackEvent({
    category: categoryTypes.ACCOUNT_RECOVERY_OTP,
    action: actionTypes.ERROR,
    label: `Submit OTP code: ${code}`,
  });
};

const AccountRecoveryOtpPage = () => {
  const [userData] = useGlobal(accountRecoveryUserData);
  const [requestingNewCode, setRequestingNewCode] = useState(false);
  const [message, setMessage] = useState(undefined);

  const history = useHistory();

  if (!userData) {
    reportError("Account Recovery - email or last name does not exist!");
  }

  const { email, lastName } = userData || {};

  const submitHandler = async ({ code }, { setSubmitting }) => {
    setSubmitting(true);
    setMessage(undefined);
    trackFormSubmit();
    const ioBlackbox = getBlackBoxToken(OTP_FORM_ID);
    try {
      const response = await otpService.verifyOtp(code, email, ioBlackbox);
      if (response.ok) {
        const { forgotten_password_token: token } = await response.json();
        history.push({
          pathname: getResetPasswordLinkNoToken(),
          state: { params: { token }, viaAccountRecovery: true },
        });
        trackFormSubmitSuccess();
      } else {
        const error = await response.json();
        throw error.code;
      }
    } catch (error) {
      trackFormSubmitError(error);
      setSubmitting(false);
      setMessage(getErrorMessage(error));
    }
  };

  const handleRequestNewCode = async (event) => {
    event && event.preventDefault();
    trackRequestCode(actionTypes.CODE_REQUESTED);
    setRequestingNewCode(true);

    try {
      const response = await otpService.requestAccountRecoveryOtp(
        email,
        lastName
      );
      if (response.ok) {
        trackRequestCode(actionTypes.SUCCESS);
        setRequestingNewCode(false);
        setMessage({
          type: NotificationStatuses.SUCCESS_TYPE,
          text: newCodeMessage,
        });
      } else {
        throw Error;
      }
    } catch (error) {
      trackRequestCode(actionTypes.ERROR);
      setRequestingNewCode(false);
      setMessage({
        type: NotificationStatuses.ERROR_TYPE,
        text: newCodeErrorMessage,
      });
    }
  };

  return (
    <PageFormat>
      <div className="c-hero c-hero--center">
        <Display4 element="h1">{"We've sent you a code"}</Display4>
      </div>
      <div className="ln-u-push-top-sm">
        <p style={{ whiteSpace: "break-spaces" }}>
          If there is a mobile number linked to your account, we've sent you a
          code via SMS.
        </p>
        <p style={{ whiteSpace: "break-spaces" }}>
          Please enter the code to continue.
        </p>
      </div>
      <OtpForm
        onRequestCode={handleRequestNewCode}
        onSubmit={submitHandler}
        isRequestingCode={requestingNewCode}
        notification={message}
      />
    </PageFormat>
  );
};

export default AccountRecoveryOtpPage;
